import React, { useMemo, useState } from 'react';
import AppBar from '@material-ui/core/AppBar';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import ViewArrayRoundedIcon from '@material-ui/icons/ViewArrayRounded';
import LogRocket from 'logrocket';
import strings from 'strings';
import { FPIconButton, When } from 'UI/components/atoms';
import TabPanel from 'UI/components/atoms/TabPanel';
import { LogRocketCustomEvents } from 'UI/constants/LogRocketCustomEvents';
import { colors } from 'UI/res';

import { OrganizeTabsDrawer } from './OrganizeTabsDrawer';
import { useTabsHandler } from './TabsView.hooks';
import { useStyles, useTabContentStyles, useTabStyles } from './TabsView.styled';
import {
  /**
   * @typedef {Object} TabsViewProps
   * @property {string} [className]
   * @property {'start' | 'center' | 'block'} content
   * @property {boolean} [enableUrlNavigation]
   * @property {number | string} [initialTab]
   * @property {Function} [onChangeTabIndex]
   * @property {string | null} [organizationStorageKey]
   * @property {string} [organizationSubheader]
   * @property {number | string | null} [padding]
   * @property {number | string | null} [panelHeight]
   * @property {string} [paramKey]
   * @property {number} [selectedTab]
   * @property {Object} tabPanelProps
   * @property {Object} tabProps
   * @property {Object[]} tabs
   * @property {Object} tabsContainerProps
   * @property {boolean // To change default behavior of using the selected tab value as an index, and instead use any given value. Useful when tabs order needs to be dynamic and logic depends on the current tab} useIndexes
   */
  a11yProps,
  buildInitialOrder,
  checkForIllegalModeChange,
  ComponentStateMode,
  getModeFromProps,
  getTabKey,
  mergeVisibleTabOrder,
  orderTabs
} from './TabsView.utils';

/**
 * @param {TabsViewProps} props
 */
const TabsView = props => {
  const {
    content,
    enableUrlNavigation,
    initialTab,
    onChangeTabIndex,
    organizationStorageKey,
    organizationSubheader,
    padding,
    panelHeight,
    paramKey,
    selectedTab: selectedTabProp,
    tabPanelProps,
    tabProps,
    tabs,
    tabsContainerProps,
    useIndexes,
    ...rest
  } = props;

  /** Styles settings */
  const classes = useStyles();
  const tabClasses = useTabStyles();
  const tabContentClasses = useTabContentStyles({ panelHeight, padding });

  const [stateMode] = useState(() => getModeFromProps(props));
  checkForIllegalModeChange(props, stateMode);

  const [orderedTabIds, setOrderedTabIds] = useState(() =>
    buildInitialOrder({ organizationStorageKey, tabs }).map(getTabKey)
  );
  const orderedTabs = useMemo(() => orderTabs({ tabs, orderedTabIds }), [orderedTabIds, tabs]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const firstTab = !useIndexes && orderedTabs.length > 0 ? orderedTabs[0].id : 0;
  const initialValue =
    stateMode === ComponentStateMode.Uncontrolled ? initialTab || firstTab : null;

  const { handleTabChange: setInternalValue, selectedTab: internalValue } = useTabsHandler(
    orderedTabs,
    {
      enableUrlNavigation,
      useIndexes,
      initialValue,
      paramKey
    }
  );

  const currentValue =
    stateMode === ComponentStateMode.Uncontrolled ? internalValue : selectedTabProp;

  const selectedTab = useIndexes ? Number(currentValue) : currentValue;
  const visibleTabs = orderedTabs.filter(({ visible = true }) => visible);

  const handleTabChange = (event, newTab = 0) => {
    if (stateMode === ComponentStateMode.Controlled) {
      onChangeTabIndex && onChangeTabIndex(event, newTab);
    } else {
      setInternalValue(event, newTab);
    }
  };

  const handleSaveOrder = updatedVisibleTabs => {
    const updatedTabs = mergeVisibleTabOrder({
      orderedTabs,
      visibleTabs: updatedVisibleTabs
    });

    try {
      window.localStorage.setItem(
        organizationStorageKey,
        JSON.stringify(updatedTabs.map(getTabKey))
      );
      LogRocket.track(LogRocketCustomEvents.Profile.OrganizeTabs);
    } catch (e) {
      // ignore
    }

    setOrderedTabIds(updatedTabs.map(getTabKey));
    setDrawerOpen(false);
  };

  return (
    <div className={tabClasses.wrapper} {...rest}>
      <AppBar classes={classes} position="static" color="default">
        <Box display="flex" alignItems="center" width="100%">
          <Tabs
            aria-label="tabs"
            className={tabClasses.fullWidth}
            indicatorColor="primary"
            onChange={handleTabChange}
            scrollButtons="on"
            textColor="primary"
            value={selectedTab}
            variant="scrollable"
            {...tabsContainerProps}
          >
            {visibleTabs.map((tab, index) => {
              const { icon, id, label } = tab;
              const isTabSelected = selectedTab === (useIndexes ? index : id);
              const styledIcon = {
                ...icon,
                props: { fill: isTabSelected ? colors.success : colors.iconInactive }
              };

              return (
                <Tab
                  className={tabClasses.tab}
                  icon={icon ? styledIcon : null}
                  key={label}
                  label={label}
                  value={useIndexes ? index : id}
                  {...a11yProps({ id, index, label })}
                  {...tab.tabProps}
                  {...tabProps}
                />
              );
            })}
          </Tabs>

          <When condition={organizationStorageKey}>
            <FPIconButton
              onClick={() => setDrawerOpen(true)}
              tooltipProps={{
                title: strings.inventoryProfiles.sections.tabs.organize.title
              }}
            >
              <ViewArrayRoundedIcon />
            </FPIconButton>
          </When>
        </Box>
      </AppBar>

      {visibleTabs.map(({ id, label }, index) => {
        const tabToRender = tabs.find(t => (t?.id || t?.label) === (id || label)) || null;

        return tabToRender ? (
          <TabPanel
            classes={tabContentClasses}
            content={content}
            index={useIndexes ? index : id}
            key={tabToRender.label}
            value={selectedTab}
            {...tabToRender?.tabPanelProps}
            {...tabPanelProps}
          >
            {tabToRender.view}
          </TabPanel>
        ) : null;
      })}

      <When condition={organizationStorageKey && drawerOpen}>
        <OrganizeTabsDrawer
          onClose={() => setDrawerOpen(false)}
          onSave={handleSaveOrder}
          subheader={organizationSubheader}
          tabs={visibleTabs}
        />
      </When>
    </div>
  );
};

TabsView.defaultProps = {
  content: 'center',
  enableUrlNavigation: false,
  initialTab: undefined,
  onChangeTabIndex: undefined,
  padding: undefined,
  panelHeight: undefined,
  paramKey: 'tab',
  selectedTab: undefined,
  tabPanelProps: undefined,
  tabProps: undefined,
  tabsContainerProps: undefined,
  useIndexes: true
};

export default TabsView;
